import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getErrorResponseData, is404Response } from '@app/routing/Session/ErrorResponse'

import { CreateTradingAccountCampaignFormProps } from '@pages/Traders-Room/Trading-Accounts/NewTradingAccount/CreateTradingAccountCampaign.types'
import {
  GenericReferralType,
  shouldHideReferral,
} from '@pages/Traders-Room/Trading-Accounts/NewTradingAccount/CreateTradingAccountCampaign.utils'

import { ResponseError, useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'
import { debounce } from '@utils/debounce'

export const useValidateAccountIntroducingBroker = (
  props: CreateTradingAccountCampaignFormProps,
  setAllFieldsDisabled: (value: boolean) => void,
  setIsValidatingIBCode: (value: boolean) => void,
  setCurrentIBValidation: (value: CreateTradingAccountCampaignFormProps['ibValidation']) => void
) => {
  const { values, setFieldValue } = props
  const { t } = useTranslation()
  const apiClient = useApiClient(ClientApiClient)
  const [ibCodeApiError, setIBCodeApiError] = useState<string>()
  const lastValidatedRef = useRef<string | undefined>(undefined)
  const referralOptionIdRef = useRef(values.referralOptionId)

  const shouldHideReferralRef = useRef(shouldHideReferral(props))

  useEffect(() => {
    referralOptionIdRef.current = values.referralOptionId
    shouldHideReferralRef.current = shouldHideReferral(props)
  })

  const debouncedValidate = useMemo(
    () =>
      debounce(async (ibCode: string) => {
        const validationKey = `${referralOptionIdRef.current}:${ibCode}`

        if (lastValidatedRef.current === validationKey) {
          return
        }

        setIBCodeApiError(undefined)
        setIsValidatingIBCode(true)

        try {
          const response = await apiClient.validateAccountIntroducingBrokerByCode(ibCode)

          if (
            shouldHideReferralRef.current ||
            referralOptionIdRef.current === GenericReferralType.None
          ) {
            return
          }

          setCurrentIBValidation(response)

          lastValidatedRef.current = validationKey

          if (response.isCustomSpreadMarkup) {
            setFieldValue('markup', false)
          }

          if (!response.isActive) {
            setAllFieldsDisabled(true)
            setIBCodeApiError(t('Validation.IB code is disabled'))
            return
          }

          setFieldValue('introducingBroker', response)
          setAllFieldsDisabled(false)
        } catch (error: unknown) {
          if (
            shouldHideReferralRef.current ||
            referralOptionIdRef.current === GenericReferralType.None
          ) {
            return
          }

          const { errorMessage } = getErrorResponseData(error as ResponseError)

          setAllFieldsDisabled(true)

          setIBCodeApiError(is404Response(error) ? t('Referral code does not exist') : errorMessage)
        } finally {
          setIsValidatingIBCode(false)
        }
      }, 1500),
    [apiClient]
  )

  useEffect(() => {
    setIBCodeApiError(undefined)

    if (shouldHideReferral(props) || values.referralOptionId === GenericReferralType.None) {
      debouncedValidate.cancel()
      lastValidatedRef.current = undefined
      setIsValidatingIBCode(false)
      setAllFieldsDisabled(false)

      return
    }

    if (values.ibCode) {
      setAllFieldsDisabled(true)
      debouncedValidate(values.ibCode)
    } else {
      lastValidatedRef.current = undefined
      setIsValidatingIBCode(false)
    }

    return () => debouncedValidate.cancel()
  }, [
    values.ibCode,
    values.referralOptionId,
    props.hasAnyLiveTA,
    props.account?.introducingBroker,
    props.ibValidation?.isActive,
    debouncedValidate,
  ])

  return { ibCodeApiError }
}
